// src/pages/quality/DynamicAssessmentsPage.jsx
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import DynamicsAssessmentTypeTable from '../../components/quality/Dynamics/DynamicsAssessmentTypeTable';
import DynamicsImpactTable from '../../components/quality/Dynamics/DynamicsImpactTable';
import DynamicsCriteriaTable from '../../components/quality/Dynamics/DynamicsCriteriaTable';
import DynamicsOperatorTable from '../../components/quality/Dynamics/DynamicsOperatorTable';
import DynamicsReceiptChannelTable from '../../components/quality/Dynamics/DynamicsReceiptChannelTable';
import DynamicsServicesAssessmentsTable from '../../components/quality/Dynamics/DynamicsServicesAssessmentsTable';

import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';




const DynamicAssessmentsPage = () => {
  const { projects } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImpact, setSelectedImpact] = useState('');
  const [selectedImpactForImpactTable, setSelectedImpactForImpactTable] = useState('High');
  const [months, setMonths] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [periodType, setPeriodType] = useState('week');

  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const projectId = pathParts[pathParts.length - 1];
  const project = projects.find(p => p.id === parseInt(projectId));

  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;

  useEffect(() => {
    // Находим проект по ID из последнего сегмента URL
    const project = projects.find(p => p.id === parseInt(projectId));
    setSelectedProject(project);
  }, [projects, projectId]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!selectedProject) return;

      try {
        setLoading(true);

        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

        // Загружаем месяцы для выпадающего списка
        const monthsResponse = await api.get(`/quality/assessment-months`, {
          params: { project_id: selectedProject.id }
        });
        if (monthsResponse.data.success) {
          setMonths(monthsResponse.data.months);
        }

        // Извлекаем month и year из selectedMonthYear
        let month = '';
        let year = '';
        if (selectedMonthYear) {
          const [m, y] = selectedMonthYear.split('-');
          month = m;
          year = y;
        }

        // Загружаем оценки для выбранного проекта
        const assessmentsResponse = await api.get(`/quality/assessments`, {
          params: {
            project_id: selectedProject.id,
            date_filter_from: dateFrom,
            date_filter_to: dateTo,
            month: month,
            year: year
          }
        });
        setAssessments(assessmentsResponse.data.data || []);

        // Загружаем все опции полей для проекта
        const optionsResponse = await api.get(`/field-options/all?project_id=${selectedProject.id}`);
        // Загружаем операторов
        const operatorsResponse = await api.get(`/operators?project_id=${selectedProject.id}`);
        let operatorsList = operatorsResponse.data.operators || [];
        
        // Проверяем, есть ли в assessments agent_name, которых нет в operatorsList
        const uniqueAgentNames = [...new Set(assessments.map(a => a.agent_name).filter(Boolean))];
        
        uniqueAgentNames.forEach(agentName => {
          // Проверяем, есть ли уже такой оператор в списке
          const exists = operatorsList.some(op => 
            `${op.surname} ${op.name}` === agentName
          );
          
          if (!exists) {
            // Разделяем имя на фамилию и имя
            const nameParts = agentName.trim().split(' ');
            if (nameParts.length >= 2) {
              const surname = nameParts[0];
              const name = nameParts[1];
              
              operatorsList.push({
                id: -1, // временный ID для отсутствующих в базе операторов
                surname: surname,
                name: name
              });
            }
          }
        });
        
        if (optionsResponse.data.success) {
          setFieldOptions(prev => ({
            ...optionsResponse.data.data,
            operators: operatorsList
          }));
        }

        // Загружаем все критерии проекта
        const criteriaResponse = await api.get(`/quality/criteria?project_id=${selectedProject.id}`);
        if (criteriaResponse.data.success) {
          setCriteria(criteriaResponse.data.data || []);
        }

        if (assessmentsResponse.data && assessmentsResponse.data.filters) {
          let shouldUpdate = false;

          const serverFrom = assessmentsResponse.data.filters.date_filter_from || getDefaultDateFrom();
          const serverTo = assessmentsResponse.data.filters.date_filter_to || getDefaultDateTo();

          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);

          // Проверяем, нужно ли обновить фильтры
          if (inputFrom && inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({target: {name: 'date_filter_from', value: inputFrom}});
            shouldUpdate = true;
          }
          if (inputTo && inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({target: {name: 'date_filter_to', value: inputTo}});
            shouldUpdate = true;
          }

          // Обновляем данные, если нужно
          if (shouldUpdate) {
            setAssessments(assessmentsResponse.data.data || []);
            return;
          }

          // Сбрасываем фильтр по месяцу, если используется фильтр по датам
          if (assessmentsResponse.data.filters.date_filter_from && assessmentsResponse.data.filters.date_filter_to) {
            setSelectedMonthYear('');
          }
          
          // Обновляем выбранный месяц и год из фильтров
          if (assessmentsResponse.data.filters.month && assessmentsResponse.data.filters.year) {
            const monthYearValue = `${assessmentsResponse.data.filters.month}-${assessmentsResponse.data.filters.year}`;
            if (monthYearValue !== selectedMonthYear) {
              setSelectedMonthYear(monthYearValue);
            }
          }
        }
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [selectedProject, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);

  // Функция для получения названия месяца по номеру
  const getMonthName = (monthNumber) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[monthNumber - 1] || '';
  };

  // Формируем опции для выпадающего списка
  const monthYearOptions = months.map(m => ({
    value: `${m.month}-${m.year}`,
    label: `${getMonthName(m.month)} ${m.year}`
  }));

  // Функция для получения первой даты недели (понедельник)
  const getMonday = (date) => {
    const dateCopy = new Date(date);
    const day = dateCopy.getDay();
    const diff = dateCopy.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(dateCopy.setDate(diff));
  };

  // Функция для получения последней даты недели (воскресенье)
  const getSunday = (date) => {
    const monday = getMonday(date);
    return new Date(monday.setDate(monday.getDate() + 6));
  };

  // Функция для форматирования даты в строку 'DD.MM.YYYY'
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Распределяем оценки по неделям/месяцам
  const assessmentsByWeek = (() => {
    if (!assessments || assessments.length === 0) return {};

    const dates = assessments.map(assessment => new Date(assessment.created_at));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    const firstMonday = getMonday(minDate);
    const lastSunday = getSunday(maxDate);

    const result = {};

    if (periodType === 'week') {
      let currentWeekStart = new Date(firstMonday);
      while (currentWeekStart <= lastSunday) {
        const weekStart = new Date(currentWeekStart);
        // Устанавливаем время старта на 00:00:00.000
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        // Устанавливаем время на конец дня воскресенья (23:59:59.999)
        weekEnd.setHours(23, 59, 59, 999);
        const weekLabel = `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
        result[weekLabel] = [];

        assessments.forEach(assessment => {
          const assessmentDate = new Date(assessment.created_at);
          if (assessmentDate >= weekStart && assessmentDate <= weekEnd) {
            result[weekLabel].push(assessment);
          }
        });

        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }
      console.log("result: ", result)
    } else if (periodType === 'month') {
      const monthsMap = new Map();
      assessments.forEach(assessment => {
        const date = new Date(assessment.created_at);
        const year = date.getFullYear();
        const month = date.getMonth();
        const key = `${year}-${month}`;
        const monthName = new Date(year, month, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
        if (!monthsMap.has(key)) {
          monthsMap.set(key, { year, month, label: monthName });
        }
      });
      const sortedMonths = Array.from(monthsMap.values()).sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

      sortedMonths.forEach(m => {
        const monthLabel = m.label;
        result[monthLabel] = [];
        assessments.forEach(assessment => {
          const date = new Date(assessment.created_at);
          if (date.getFullYear() === m.year && date.getMonth() === m.month) {
            result[monthLabel].push(assessment);
          }
        });
      });
    }
    return result;
  })();

  console.log(assessments.length)
  console.log('assessmentsByWeek: ', assessmentsByWeek)
  console.log('assessmentsByWeek count: ', assessmentsByWeek.length)

  return (
    <div className="main-content">
      <h1>Динамическая аналитика оценок</h1>

      {loading ? (
        <div>Загрузка...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
          <div>
            <p>Проект: {selectedProject ? selectedProject.name_text : 'Не выбран'}</p>
            <p>Загружено проверок: {assessments.length}</p>
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <label className="form-label">Дата от (с секундами)</label>
                <input
                    type="datetime-local"
                    name="date_filter_from"
                    className="form-control form-control-sm"
                    value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
                    onChange={handleDateChange}
                    onBlur={handleDateBlur}
                    step="1"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Дата до (с секундами)</label>
                <input
                    type="datetime-local"
                    name="date_filter_to"
                    className="form-control form-control-sm"
                    value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
                    onChange={handleDateChange}
                    onBlur={handleDateBlur}
                    step="1"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Фильтр по месяцу</label>
                <select
                  className="form-control form-control-sm"
                  value={selectedMonthYear}
                  onChange={(e) => setSelectedMonthYear(e.target.value)}
                >
                  <option value="">Все месяцы</option>
                  {monthYearOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Период</label>
                <select
                  className="form-control form-control-sm"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                >
                  <option value="month">Месяц</option>
                  <option value="week">Неделя</option>
                </select>
              </div>
            </div>

            <div className="tables-container">
              <div className="tables-row">
                <DynamicsAssessmentTypeTable
                    assessmentsByWeek={assessmentsByWeek}
                    fieldOptions={fieldOptions?.assessment_type || []}
                    periodType={periodType}
                />
              </div>
              <div className="tables-row">
                <DynamicsReceiptChannelTable
                    assessmentsByWeek={assessmentsByWeek}
                    fieldOptions={fieldOptions?.receipt_channel || []}
                    periodType={periodType}
                />
              </div>
              <div className="tables-row">
                <DynamicsImpactTable
                    assessmentsByWeek={assessmentsByWeek}
                    fieldOptions={fieldOptions?.impact || []}
                    periodType={periodType}
                />
              </div>
              {/*<div className="tables-row">*/}
              {/*  <DynamicsServicesAssessmentsTable*/}
              {/*      assessments={assessments}*/}
              {/*      periodType={periodType}*/}
              {/*  />*/}
              {/*</div>*/}
              <div className="tables-row">
                <DynamicsCriteriaTable
                    assessmentsByWeek={assessmentsByWeek}
                    criteria={criteria}
                    periodType={periodType}
                />
              </div>
              <div className="tables-row">
                <DynamicsOperatorTable
                    assessmentsByWeek={assessmentsByWeek}
                    fieldOptions={fieldOptions?.operators || []}
                    periodType={periodType}
                />
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default DynamicAssessmentsPage;
